import React, { useState, useEffect } from 'react';
import { Switch } from './Switch';
import { formatBelgianReference, isStructuredReference, validateBelgianReference } from '../../utils/belgianReference';

interface StructuredReferenceFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

export default function StructuredReferenceField({
  value,
  onChange,
  disabled = false,
  error = false
}: StructuredReferenceFieldProps) {
  const [isStructured, setIsStructured] = useState(false);

  // Auto-detect structured reference format
  useEffect(() => {
    if (value) {
      const shouldBeStructured = isStructuredReference(value);
      setIsStructured(shouldBeStructured);
      
      // If it's a structured reference but not properly formatted, format it
      if (shouldBeStructured && !value.startsWith('+++')) {
        onChange(formatBelgianReference(value));
      }
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    
    if (isStructured) {
      // Format as Belgian structured reference
      newValue = formatBelgianReference(newValue);
    }
    
    onChange(newValue);
  };

  const isValid = !value || !isStructured || validateBelgianReference(value);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs text-[#c8c2bd]">Reference</label>
        <Switch
          checked={isStructured}
          onChange={(checked) => {
            setIsStructured(checked);
            if (checked && value) {
              // Convert to structured format when switching to structured
              onChange(formatBelgianReference(value));
            }
          }}
          label="Structured"
          disabled={disabled}
        />
      </div>
      
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          disabled={disabled}
          maxLength={isStructured ? 21 : 35}
          placeholder={isStructured ? '+++123/1234/12345+++' : 'Enter reference number'}
          className={`w-full px-4 py-2.5 text-sm bg-black/50 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-[#c8c2bd] ${
            !isValid || error
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-800'
          } ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        />
        {isStructured && !isValid && value && (
          <p className="mt-1 text-xs text-red-500">
            Invalid structured reference format
          </p>
        )}
      </div>
    </div>
  );
}